import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRightLeft,
  Briefcase,
  Check,
  ClipboardCheck,
  CreditCard,
  FileText,
  History,
  Pencil,
  Phone,
  ShieldCheck,
  User,
  UserX,
  Users,
  X,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Badge } from "../components/Badge";
import { EmployeeDocumentsTab } from "../components/EmployeeDocumentsTab";
import { ErrorPanel, Loading } from "../components/Loading";
import { AIGenerateButton } from "../components/ui/AIGenerateButton";
import { BilingualText } from "../components/ui/BilingualText";
import { EditableEmployeeAvatar } from "../components/ui/EditableEmployeeAvatar";
import { PositionPicker } from "../components/ui/PositionPicker";
import { EntityDrawer } from "../components/ui/EntityDrawer";
import { api, ApiError } from "../lib/api";
import type {
  BankAccountDTO,
  EmergencyContactDTO,
  EmployeeDependentDTO,
  EmployeeEventDTO,
  EmployeeMasterFile,
  PassportDTO,
} from "../lib/api";
import { useLocale } from "../lib/i18n";

// 8-tab master file (M11.3 → M21.8 adds the Documents tab + identity
// expiry tracking + the Current Contract section).

const TABS = [
  { key: "personal", labelKey: "employee_master.tab_personal", icon: User },
  { key: "identity", labelKey: "employee_master.tab_identity", icon: ShieldCheck },
  { key: "contact", labelKey: "employee_master.tab_contact", icon: Phone },
  { key: "family", labelKey: "employee_master.tab_family", icon: Users },
  { key: "banking", labelKey: "employee_master.tab_banking", icon: CreditCard },
  { key: "documents", labelKey: "employee_master.tab_documents", icon: FileText },
  { key: "compliance", labelKey: "employee_master.tab_compliance", icon: ClipboardCheck },
  { key: "history", labelKey: "employee_master.tab_history", icon: History },
] as const;
type TabKey = (typeof TABS)[number]["key"];

// A date within 30 days (past or future) is a compliance flag.
function _expiresSoon(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const expiry = new Date(dateStr).getTime();
  return expiry <= Date.now() + 30 * 24 * 60 * 60 * 1000;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </span>
      <span className="text-sm text-slate-900">
        {value === null || value === undefined || value === "" ? (
          <span className="italic text-slate-400">—</span>
        ) : (
          value
        )}
      </span>
    </div>
  );
}

// EditableField — single string-valued field with click-to-edit + save
// on blur or Enter. Cancels on Escape. Calls `onSave(newValue)` and
// shows the API error message if the mutation fails.
//
// `rtl` switches the input's text direction (Arabic name fields).
// `multiline` swaps the <input> for a <textarea> (address lines).
function EditableField({
  label,
  value,
  fieldName,
  employeeId,
  rtl = false,
  multiline = false,
}: {
  label: string;
  value: string | null;
  fieldName: string;
  employeeId: string;
  rtl?: boolean;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: (newValue: string) =>
      api.updateEmployee(employeeId, { [fieldName]: newValue || null }),
    onSuccess: () => {
      setEditing(false);
      setErrorMsg(null);
      qc.invalidateQueries({ queryKey: ["employee-master", employeeId] });
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setErrorMsg(e.detail);
      else setErrorMsg(String(e));
    },
  });

  const startEdit = () => {
    setDraft(value ?? "");
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setErrorMsg(null);
  };
  const save = () => {
    if (draft === (value ?? "")) {
      cancel();
      return;
    }
    mutation.mutate(draft);
  };

  if (!editing) {
    return (
      <div className="flex flex-col gap-1" data-testid={`editable-${fieldName}`}>
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {label}
        </span>
        <button
          type="button"
          onClick={startEdit}
          className="group flex items-center gap-1.5 rounded text-start text-sm text-slate-900 hover:bg-slate-50"
        >
          <span dir={rtl ? "rtl" : "ltr"} className="flex-1">
            {value === null || value === "" ? (
              <span className="italic text-slate-400">—</span>
            ) : (
              value
            )}
          </span>
          <Pencil className="h-3.5 w-3.5 opacity-0 transition group-hover:opacity-100" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1" data-testid={`editable-${fieldName}`}>
      <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
        {label}
      </span>
      <div className="flex items-start gap-1">
        {multiline ? (
          <textarea
            autoFocus
            dir={rtl ? "rtl" : "ltr"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
            }}
            rows={2}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        ) : (
          <input
            autoFocus
            type="text"
            dir={rtl ? "rtl" : "ltr"}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") cancel();
            }}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm"
          />
        )}
        <SaveCancelButtons
          onSave={save}
          onCancel={cancel}
          saving={mutation.isPending}
        />
      </div>
      {errorMsg && (
        <p className="text-xs text-rose-600">{errorMsg}</p>
      )}
    </div>
  );
}

function SaveCancelButtons({
  onSave,
  onCancel,
  saving,
}: {
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="rounded p-1 text-emerald-600 hover:bg-emerald-50"
        aria-label={t("employee_master.save_aria")}
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded p-1 text-slate-500 hover:bg-slate-100"
        aria-label={t("employee_master.cancel_aria")}
      >
        <X className="h-4 w-4" />
      </button>
    </>
  );
}

function PersonalTab({ emp }: { emp: EmployeeMasterFile }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-6">
      <EditableField label={t("employee_master.fld_first_name_en")} value={emp.first_name_en} fieldName="first_name_en" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_first_name_ar")} value={emp.first_name_ar} fieldName="first_name_ar" employeeId={emp.id} rtl />
      <EditableField label={t("employee_master.fld_middle_name_en")} value={emp.middle_name_en} fieldName="middle_name_en" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_middle_name_ar")} value={emp.middle_name_ar} fieldName="middle_name_ar" employeeId={emp.id} rtl />
      <EditableField label={t("employee_master.fld_last_name_en")} value={emp.last_name_en} fieldName="last_name_en" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_last_name_ar")} value={emp.last_name_ar} fieldName="last_name_ar" employeeId={emp.id} rtl />
      <Field label={t("employee_master.fld_date_of_birth")} value={emp.date_of_birth} />
      <Field label={t("employee_master.fld_gender")} value={emp.gender} />
      <Field label={t("employee_master.fld_nationality")} value={emp.nationality} />
      <EditableField label={t("employee_master.fld_marital_status")} value={emp.marital_status} fieldName="marital_status" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_religion")} value={emp.religion} fieldName="religion" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_mother_tongue")} value={emp.mother_tongue} fieldName="mother_tongue" employeeId={emp.id} />
      {/* photo_url is an internal blob URI (localfs:// or r2://) —
          editing it as text doesn't make sense. The photo is uploaded
          + removed via the avatar in the page header instead. */}
    </div>
  );
}

// One identity-document expiry field — the EditableField plus a
// rose "Expires soon" badge when the date is within 30 days.
function ExpiryField({
  label,
  value,
  fieldName,
  employeeId,
}: {
  label: string;
  value: string | null;
  fieldName: string;
  employeeId: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <EditableField
        label={label}
        value={value}
        fieldName={fieldName}
        employeeId={employeeId}
      />
      {_expiresSoon(value) && (
        <Badge tone="rose">{t("employee_master.expires_within_30")}</Badge>
      )}
    </div>
  );
}

function IdentityTab({ emp }: { emp: EmployeeMasterFile }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-6">
        <EditableField
          label={emp.is_saudi_national ? t("employee_master.fld_national_id") : t("employee_master.fld_iqama")}
          value={emp.national_id_or_iqama}
          fieldName="national_id_or_iqama"
          employeeId={emp.id}
        />
        <EditableField label={t("employee_master.fld_mol_number")} value={emp.mol_number} fieldName="mol_number" employeeId={emp.id} />
        <EditableField
          label={t("employee_master.fld_driver_license")}
          value={emp.driver_license_number}
          fieldName="driver_license_number"
          employeeId={emp.id}
        />
      </div>

      {/* M21.8 — identity issue / expiry tracking */}
      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          {t("employee_master.section_identity_validity")}
        </h3>
        <div className="grid grid-cols-2 gap-6">
          <EditableField
            label={t("employee_master.fld_national_id_issued")}
            value={emp.national_id_issued}
            fieldName="national_id_issued"
            employeeId={emp.id}
          />
          <ExpiryField
            label={t("employee_master.fld_national_id_expiry")}
            value={emp.national_id_expiry}
            fieldName="national_id_expiry"
            employeeId={emp.id}
          />
          <EditableField
            label={t("employee_master.fld_iqama_issued")}
            value={emp.iqama_issued}
            fieldName="iqama_issued"
            employeeId={emp.id}
          />
          <ExpiryField
            label={t("employee_master.fld_iqama_expiry")}
            value={emp.iqama_expiry}
            fieldName="iqama_expiry"
            employeeId={emp.id}
          />
          <EditableField
            label={t("employee_master.fld_driver_license_issued")}
            value={emp.driver_license_issued}
            fieldName="driver_license_issued"
            employeeId={emp.id}
          />
          <ExpiryField
            label={t("employee_master.fld_driver_license_expiry")}
            value={emp.driver_license_expiry}
            fieldName="driver_license_expiry"
            employeeId={emp.id}
          />
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">{t("employee_master.section_passports")}</h3>
        {emp.passports.length === 0 ? (
          <p className="text-sm italic text-slate-500">{t("employee_master.no_passports")}</p>
        ) : (
          <table className="w-full border border-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-start">{t("employee_master.col_number")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_country")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_issued")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_expires")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_primary")}</th>
              </tr>
            </thead>
            <tbody>
              {emp.passports.map((p: PassportDTO) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono">{p.passport_number}</td>
                  <td className="px-3 py-2">{p.issuing_country}</td>
                  <td className="px-3 py-2">{p.issued_date}</td>
                  <td className="px-3 py-2">{p.expiry_date}</td>
                  <td className="px-3 py-2">
                    {p.is_primary ? <Badge tone="green">{t("employee_master.yes")}</Badge> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ContactTab({ emp }: { emp: EmployeeMasterFile }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-2 gap-6">
      <EditableField label={t("employee_master.fld_phone")} value={emp.phone_number} fieldName="phone_number" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_alternate_phone")} value={emp.alternate_phone} fieldName="alternate_phone" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_work_email")} value={emp.work_email} fieldName="work_email" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_personal_email")} value={emp.personal_email} fieldName="personal_email" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_current_address_line")} value={emp.current_address_line} fieldName="current_address_line" employeeId={emp.id} multiline />
      <EditableField label={t("employee_master.fld_current_address_city")} value={emp.current_address_city} fieldName="current_address_city" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_current_address_region")} value={emp.current_address_region} fieldName="current_address_region" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_permanent_address_line")} value={emp.permanent_address_line} fieldName="permanent_address_line" employeeId={emp.id} multiline />
      <EditableField label={t("employee_master.fld_permanent_address_city")} value={emp.permanent_address_city} fieldName="permanent_address_city" employeeId={emp.id} />
      <EditableField label={t("employee_master.fld_permanent_address_region")} value={emp.permanent_address_region} fieldName="permanent_address_region" employeeId={emp.id} />
    </div>
  );
}

function FamilyTab({ emp }: { emp: EmployeeMasterFile }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-8">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">{t("employee_master.section_dependents")}</h3>
        {emp.dependents.length === 0 ? (
          <p className="text-sm italic text-slate-500">{t("employee_master.no_dependents")}</p>
        ) : (
          <table className="w-full border border-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-start">{t("employee_master.col_name")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_relationship")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_dob")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_residence")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_schooling")}</th>
              </tr>
            </thead>
            <tbody>
              {emp.dependents.map((d: EmployeeDependentDTO) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    <BilingualText
                      en={d.name}
                      ar={d.name_ar}
                      secondaryClassName="text-xs text-slate-500"
                    />
                  </td>
                  <td className="px-3 py-2">{d.relationship}</td>
                  <td className="px-3 py-2">{d.date_of_birth}</td>
                  <td className="px-3 py-2">{d.residence_status}</td>
                  <td className="px-3 py-2">
                    {d.schooling_eligible ? (
                      <Badge tone="green">{t("employee_master.badge_eligible")}</Badge>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div>
        <h3 className="mb-2 text-sm font-semibold text-slate-700">
          {t("employee_master.section_emergency_contacts")}
        </h3>
        {emp.emergency_contacts.length === 0 ? (
          <p className="text-sm italic text-slate-500">{t("employee_master.no_emergency_contacts")}</p>
        ) : (
          <table className="w-full border border-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-start">{t("employee_master.col_priority")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_name")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_relationship")}</th>
                <th className="px-3 py-2 text-start">{t("employee_master.col_phone")}</th>
              </tr>
            </thead>
            <tbody>
              {emp.emergency_contacts.map((c: EmergencyContactDTO) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 tabular-nums">{c.priority}</td>
                  <td className="px-3 py-2">{c.name}</td>
                  <td className="px-3 py-2">{c.relationship}</td>
                  <td className="px-3 py-2 font-mono">{c.phone}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BankingTab({ emp }: { emp: EmployeeMasterFile }) {
  const { t } = useTranslation();
  return (
    <div>
      {emp.bank_accounts.length === 0 ? (
        <p className="text-sm italic text-slate-500">{t("employee_master.no_bank_accounts")}</p>
      ) : (
        <table className="w-full border border-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-start">{t("employee_master.col_bank")}</th>
              <th className="px-3 py-2 text-start">{t("employee_master.col_iban")}</th>
              <th className="px-3 py-2 text-start">{t("employee_master.col_holder")}</th>
              <th className="px-3 py-2 text-end">{t("employee_master.col_distribution")}</th>
              <th className="px-3 py-2 text-start">{t("employee_master.col_active")}</th>
            </tr>
          </thead>
          <tbody>
            {emp.bank_accounts.map((b: BankAccountDTO) => (
              <tr key={b.id} className="border-t border-slate-100">
                <td className="px-3 py-2">{b.bank_name}</td>
                <td className="px-3 py-2 font-mono">{b.iban}</td>
                <td className="px-3 py-2">{b.account_holder_name}</td>
                <td className="px-3 py-2 text-end tabular-nums">
                  {b.distribution_percent}%
                </td>
                <td className="px-3 py-2">
                  {b.is_active ? <Badge tone="green">{t("employee_master.badge_active")}</Badge> : <Badge tone="slate">{t("employee_master.badge_inactive")}</Badge>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// M21.8 — the Current Contract section, embedded in the Compliance tab.
function CurrentContractSection({ emp }: { emp: EmployeeMasterFile }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery({
    queryKey: ["employee-current-contract", emp.id],
    queryFn: () => api.getEmployeeCurrentContract(emp.id),
    enabled: !!emp.id,
  });

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
        <FileText className="h-4 w-4" /> {t("employee_master.current_contract")}
      </h3>
      {isLoading ? (
        <p className="text-sm text-slate-500">{t("employee_master.loading")}</p>
      ) : !data ? (
        <p className="text-sm italic text-slate-500">
          {t("employee_master.no_contract_on_file")}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <span>
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("employee_master.fld_number")}
            </span>
            <span className="ms-2 font-mono">
              {data.contract_number ?? "—"}
            </span>
          </span>
          <span>
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("employee_master.fld_status")}
            </span>
            <Badge tone={data.contract_status === "active" ? "green" : "slate"}>
              {data.contract_status ?? "—"}
            </Badge>
          </span>
          <span>
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("employee_master.fld_type")}
            </span>
            <span className="ms-2">{data.employment_type ?? "—"}</span>
          </span>
          <Link
            to={`/people/contracts/${data.id}`}
            className="font-medium text-slate-900 underline hover:text-slate-700"
          >
            {t("employee_master.open_contract_link")} →
          </Link>
        </div>
      )}
    </div>
  );
}

function ComplianceTab({ emp }: { emp: EmployeeMasterFile }) {
  const { t } = useTranslation();
  const serviceYears = (() => {
    const start = emp.original_hire_date ?? emp.hire_date;
    const startDate = new Date(start);
    const end = emp.terminated_at ? new Date(emp.terminated_at) : new Date();
    const diff = end.getTime() - startDate.getTime();
    return (diff / (365.25 * 24 * 60 * 60 * 1000)).toFixed(2);
  })();

  return (
    <div className="flex flex-col gap-6">
      <CurrentContractSection emp={emp} />
      <div className="grid grid-cols-2 gap-6">
        <Field label={t("employee_master.fld_hire_date")} value={emp.hire_date} />
        <Field
          label={t("employee_master.fld_original_hire_date")}
          value={emp.original_hire_date ?? emp.hire_date}
        />
        <Field label={t("employee_master.fld_service_years")} value={t("employee_master.service_years_unit", { years: serviceYears })} />
        <Field label={t("employee_master.fld_status")} value={<Badge tone="green">{emp.employment_status}</Badge>} />
        <Field
          label={t("employee_master.fld_first_gosi")}
          value={emp.first_gosi_contribution_date}
        />
        <div className="flex flex-col gap-1">
          <EditableField label={t("employee_master.fld_work_permit_expires")} value={emp.work_permit_expiry} fieldName="work_permit_expiry" employeeId={emp.id} />
          {_expiresSoon(emp.work_permit_expiry) && (
            <Badge tone="rose">{t("employee_master.expires_within_30")}</Badge>
          )}
        </div>
        <EditableField label={t("employee_master.fld_saudization_class")} value={emp.saudization_classification} fieldName="saudization_classification" employeeId={emp.id} />
        <Field
          label={t("employee_master.fld_terminated")}
          value={
            emp.terminated_at
              ? `${emp.terminated_at} — ${emp.termination_reason ?? t("employee_master.no_reason")}`
              : null
          }
        />
      </div>
      {emp.active_termination_id && (
        <Link
          to={`/operations/eos/${emp.active_termination_id}`}
          className="flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm hover:bg-rose-100"
        >
          <span className="font-medium text-rose-900">
            {t("employee_master.open_eos_workflow")}
            <span className="ms-2 font-normal text-rose-700">
              ({emp.active_termination_state})
            </span>
          </span>
          <span className="text-rose-700">{t("employee_master.open_eos_settlement")} →</span>
        </Link>
      )}
    </div>
  );
}

const EVENT_META: Record<
  string,
  { labelKey: string; tone: "green" | "amber" | "sky" | "slate" | "rose" }
> = {
  created: { labelKey: "employee_master.evt_created", tone: "green" },
  updated: { labelKey: "employee_master.evt_updated", tone: "sky" },
  position_assigned: { labelKey: "employee_master.evt_position_assigned", tone: "sky" },
  position_unassigned: { labelKey: "employee_master.evt_position_unassigned", tone: "amber" },
  transferred: { labelKey: "employee_master.evt_transferred", tone: "amber" },
  terminated: { labelKey: "employee_master.evt_terminated", tone: "rose" },
  rehired: { labelKey: "employee_master.evt_rehired", tone: "green" },
  bulk_reassigned: { labelKey: "employee_master.evt_bulk_reassigned", tone: "sky" },
};

function EventDiff({ event }: { event: EmployeeEventDTO }) {
  const { t } = useTranslation();
  // Render the field-by-field diff as paired before/after rows. Only
  // shows fields that actually changed (the backend strips no-ops).
  const before = event.before_json ?? {};
  const after = event.after_json ?? {};
  const keys = Array.from(
    new Set([...Object.keys(before), ...Object.keys(after)]),
  );
  if (keys.length === 0) return null;

  return (
    <table className="mt-2 w-full border border-slate-100 text-xs">
      <thead className="bg-slate-50 text-slate-500">
        <tr>
          <th className="px-2 py-1 text-start">{t("employee_master.col_field")}</th>
          <th className="px-2 py-1 text-start">{t("employee_master.col_before")}</th>
          <th className="px-2 py-1 text-start">{t("employee_master.col_after")}</th>
        </tr>
      </thead>
      <tbody>
        {keys.map((k) => (
          <tr key={k} className="border-t border-slate-100">
            <td className="px-2 py-1 font-medium text-slate-700">{k}</td>
            <td className="px-2 py-1 font-mono text-slate-500">
              {JSON.stringify((before as Record<string, unknown>)[k]) ?? "—"}
            </td>
            <td className="px-2 py-1 font-mono text-slate-900">
              {JSON.stringify((after as Record<string, unknown>)[k]) ?? "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function HistoryTab({ emp }: { emp: EmployeeMasterFile }) {
  const { t, i18n } = useTranslation();
  if (emp.recent_events.length === 0) {
    return (
      <p className="text-sm italic text-slate-500">
        {t("employee_master.no_history_events")}
      </p>
    );
  }
  return (
    <ol className="relative border-s-2 border-slate-200">
      {emp.recent_events.map((e: EmployeeEventDTO) => {
        const meta = EVENT_META[e.event_type] ?? {
          labelKey: e.event_type,
          tone: "slate" as const,
        };
        const label = EVENT_META[e.event_type] ? t(meta.labelKey) : e.event_type;
        return (
          <li
            key={e.id}
            data-event-row
            className="relative ms-6 pb-6"
          >
            <div
              className={`absolute -start-[1.7rem] mt-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-white ${
                meta.tone === "green"
                  ? "bg-emerald-500"
                  : meta.tone === "amber"
                    ? "bg-amber-500"
                    : meta.tone === "sky"
                      ? "bg-sky-500"
                      : meta.tone === "rose"
                        ? "bg-rose-500"
                        : "bg-slate-400"
              }`}
            />
            <time className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {new Date(e.occurred_at).toLocaleString(
                i18n.language === "ar" ? "ar-SA" : undefined,
              )}
            </time>
            <div className="mt-1 flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Badge tone={meta.tone}>{label}</Badge>
              {e.reason_code && (
                <span className="text-xs font-normal text-slate-500">
                  ({e.reason_code})
                </span>
              )}
            </div>
            {e.notes && (
              <p className="mt-1 text-sm text-slate-600">{e.notes}</p>
            )}
            {(e.before_json || e.after_json) && <EventDiff event={e} />}
            {e.actor_clerk_user_id && (
              <p className="mt-1 text-xs text-slate-400">
                {t("employee_master.by_actor", { actor: e.actor_clerk_user_id })}
              </p>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function TransferDialog({
  emp,
  onClose,
}: {
  emp: EmployeeMasterFile;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [positionId, setPositionId] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [reasonCode, setReasonCode] = useState("transfer");
  const [notes, setNotes] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      api.transferEmployee(emp.id, {
        new_position_id: positionId,
        effective_date: effectiveDate,
        reason_code: reasonCode,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-master", emp.id] });
      qc.invalidateQueries({ queryKey: ["org-chart"] });
      onClose();
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setErrorMsg(e.detail);
      else setErrorMsg(String(e));
    },
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("employee_master.transfer_dialog_title")}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("employee_master.cancel")}
          </button>
          <button
            type="submit"
            form="transfer-employee-form"
            disabled={mutation.isPending || !emp.current_position || !positionId}
            className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending
              ? t("employee_master.transferring")
              : t("employee_master.confirm_transfer")}
          </button>
        </div>
      }
    >
      <form
        id="transfer-employee-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!positionId) return;
          mutation.mutate();
        }}
        className="space-y-4"
      >
        {emp.current_position ? (
          <div className="rounded-lg border border-border-subtle bg-surface-sunken p-3 text-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              {t("employee_master.from_label")}
            </p>
            <p className="mt-1 font-medium text-text-primary">
              {emp.current_position.title_en} ·{" "}
              <span className="font-mono">
                {emp.current_position.position_number_display}
              </span>
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            {t("employee_master.no_current_position_cannot_transfer")}
          </div>
        )}

        <label className="block text-sm">
          <span className="font-medium text-text-primary">{t("employee_master.new_position")} *</span>
          <div className="mt-1">
            <PositionPicker
              legalEntityId={emp.legal_entity_id ?? ""}
              status="open"
              value={positionId || null}
              onChange={(p) => {
                // Don't allow picking the employee's CURRENT position —
                // a no-op transfer is invalid (the same gate the old
                // select had via .filter()).
                if (
                  p
                  && p.id === emp.current_position?.position_id
                ) {
                  return;
                }
                setPositionId(p?.id ?? "");
              }}
              placeholder={t("employee_master.position_search_placeholder")}
            />
          </div>
        </label>

        <label className="block text-sm">
          <span className="font-medium text-text-primary">{t("employee_master.effective_date")} *</span>
          <input
            required
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm"
          />
        </label>

        <label className="block text-sm">
          <span className="font-medium text-text-primary">{t("employee_master.reason")}</span>
          <select
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm"
          >
            <option value="transfer">{t("employee_master.reason_transfer")}</option>
            <option value="promotion">{t("employee_master.reason_promotion")}</option>
            <option value="reorg">{t("employee_master.reason_reorg")}</option>
          </select>
        </label>

        <label className="block text-sm">
          <span className="font-medium text-text-primary">{t("employee_master.notes")}</span>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm"
          />
        </label>

        {errorMsg && (
          <p className="rounded bg-rose-50 p-2 text-sm text-rose-700">
            {errorMsg}
          </p>
        )}
      </form>
    </EntityDrawer>
  );
}

function TerminateDialog({
  emp,
  onClose,
}: {
  emp: EmployeeMasterFile;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [lastWorkingDay, setLastWorkingDay] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [reason, setReason] = useState("");
  const [reasonCode, setReasonCode] = useState<
    "with_cause" | "without_cause" | "mutual"
  >("without_cause");
  const [noticeServed, setNoticeServed] = useState(true);
  const [notes, setNotes] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [confirm, setConfirm] = useState("");
  const qc = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: () =>
      api.terminateEmployee(emp.id, {
        last_working_day: lastWorkingDay,
        termination_reason: reason,
        reason_code: reasonCode,
        notice_period_served: noticeServed,
        notes: notes || undefined,
      }),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["employee-master", emp.id] });
      qc.invalidateQueries({ queryKey: ["org-chart"] });
      onClose();
      // M21.9 — the terminate endpoint auto-triggers the M18 EOS
      // workflow. Jump HR straight to the settlement so they can
      // follow it through to closure.
      if (updated.active_termination_id) {
        navigate(`/operations/eos/${updated.active_termination_id}`);
      }
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setErrorMsg(e.detail);
      else setErrorMsg(String(e));
    },
  });

  const employeeCode = emp.employee_code;
  const submitEnabled =
    confirm === employeeCode &&
    reason.trim().length > 0 &&
    !mutation.isPending;

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("employee_master.terminate_dialog_title")}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("employee_master.cancel")}
          </button>
          <button
            type="submit"
            form="terminate-employee-form"
            disabled={!submitEnabled}
            data-testid="terminate-dialog-submit"
            className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {mutation.isPending
              ? t("employee_master.terminating")
              : t("employee_master.terminate_action")}
          </button>
        </div>
      }
    >
      <form
        id="terminate-employee-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!submitEnabled) return;
          mutation.mutate();
        }}
        className="space-y-4"
      >
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="font-medium">
              {emp.first_name_en} {emp.last_name_en}
            </p>
            <p className="text-slate-600">
              {emp.employee_code} · {t("employee_master.hired_prefix")} {emp.hire_date}
            </p>
            {emp.current_position && (
              <p className="mt-1 text-slate-600">
                {t("employee_master.will_close_position", { title: emp.current_position.title_en })}
              </p>
            )}
          </div>

          <label className="block text-sm">
            <span className="font-medium text-slate-700">{t("employee_master.last_working_day")} *</span>
            <input
              required
              type="date"
              value={lastWorkingDay}
              onChange={(e) => setLastWorkingDay(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="block text-sm">
            <span className="flex items-center justify-between font-medium text-slate-700">
              {t("employee_master.reason")} *
              <AIGenerateButton
                kind="termination_reason"
                legalEntityId={emp.legal_entity_id ?? undefined}
                context={{
                  reason_category: reasonCode,
                  context: reason.trim() || "Employee separation",
                }}
                onAccept={(draft) => setReason(draft)}
                label={t("employee_master.reason_with_ai_label")}
              />
            </span>
            <input
              required
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("employee_master.reason_placeholder")}
              data-testid="terminate-reason-input"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-slate-700">
              {t("employee_master.reason_category")} *
            </span>
            <select
              value={reasonCode}
              onChange={(e) =>
                setReasonCode(
                  e.target.value as
                    | "with_cause"
                    | "without_cause"
                    | "mutual",
                )
              }
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="without_cause">
                {t("employee_master.cat_without_cause")}
              </option>
              <option value="with_cause">{t("employee_master.cat_with_cause")}</option>
              <option value="mutual">{t("employee_master.cat_mutual")}</option>
            </select>
            <span className="mt-1 block text-xs text-slate-500">
              {t("employee_master.cat_help")}
            </span>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={noticeServed}
              onChange={(e) => setNoticeServed(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            <span>{t("employee_master.notice_served")}</span>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-slate-700">{t("employee_master.notes")}</span>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </label>

          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm">
            <p className="font-medium text-rose-900">
              {t("employee_master.type_to_confirm", { code: employeeCode })}
            </p>
            <input
              type="text"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="terminate-confirm-input"
              className="mt-1 w-full rounded-lg border border-rose-300 px-3 py-2 text-sm font-mono"
            />
          </div>

        {errorMsg && (
          <p className="rounded bg-rose-50 p-2 text-sm text-rose-700">
            {errorMsg}
          </p>
        )}
      </form>
    </EntityDrawer>
  );
}

function CurrentContractLink({ employeeId }: { employeeId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ["employee-current-contract", employeeId],
    queryFn: () => api.getEmployeeCurrentContract(employeeId),
    enabled: !!employeeId,
  });
  if (!data) return null;
  return (
    <Link
      to={`/people/contracts/${data.id}`}
      className="mt-2 inline-flex items-center gap-1.5 text-sm text-brand-blue font-medium hover:underline"
    >
      <FileText className="h-4 w-4" />
      {t("employee_master.contract_label")} {data.contract_number ?? ""} →
    </Link>
  );
}

function Header({
  emp,
  onTransfer,
  onTerminate,
}: {
  emp: EmployeeMasterFile;
  onTransfer: () => void;
  onTerminate: () => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const fullName =
    locale === "ar"
      ? `${emp.first_name_ar} ${emp.last_name_ar}`
      : `${emp.first_name_en} ${emp.last_name_en}`;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <EditableEmployeeAvatar
            employeeId={emp.id}
            photoUrl={emp.photo_url}
            firstName={emp.first_name_en}
            lastName={emp.last_name_en}
            size={80}
          />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {emp.employee_code} ·{" "}
              {emp.is_saudi_national
                ? t("employee_master.saudi_label")
                : t("employee_master.non_saudi_label")}
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900 truncate">{fullName}</h1>
            {emp.current_position ? (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-slate-700">
                <Briefcase className="h-4 w-4 text-slate-400" />
                {emp.current_position.title_en} ·{" "}
                <span className="font-mono">
                  {emp.current_position.position_number_display}
                </span>
              </p>
            ) : (
              <p className="mt-2 text-sm italic text-slate-500">
                {t("employee_master.no_current_position")}
              </p>
            )}
            <CurrentContractLink employeeId={emp.id} />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="rounded-lg bg-slate-50 px-4 py-3 text-end">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("employee_master.header_status")}
            </p>
            <p className="mt-1 text-lg font-bold capitalize">
              {emp.employment_status}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onTransfer}
              disabled={!emp.current_position || emp.employment_status === "terminated"}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              <ArrowRightLeft className="h-4 w-4" /> {t("employee_master.transfer_btn")}
            </button>
            <button
              type="button"
              onClick={onTerminate}
              disabled={emp.employment_status === "terminated"}
              className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-40"
            >
              <UserX className="h-4 w-4" /> {t("employee_master.terminate_btn")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function EmployeeMasterFilePage() {
  const { t } = useTranslation();
  const { employeeId } = useParams<{ employeeId: string }>();
  const [active, setActive] = useState<TabKey>("personal");
  const [showTransfer, setShowTransfer] = useState(false);
  const [showTerminate, setShowTerminate] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["employee-master", employeeId],
    queryFn: () => api.getEmployeeMaster(employeeId!),
    enabled: !!employeeId,
  });

  if (isLoading) return <Loading />;
  if (error) return <ErrorPanel message={(error as Error).message} />;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-4">
      <Header
        emp={data}
        onTransfer={() => setShowTransfer(true)}
        onTerminate={() => setShowTerminate(true)}
      />
      {showTransfer && (
        <TransferDialog emp={data} onClose={() => setShowTransfer(false)} />
      )}
      {showTerminate && (
        <TerminateDialog emp={data} onClose={() => setShowTerminate(false)} />
      )}

      <div
        role="tablist"
        className="flex items-center gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-white p-1"
      >
        {TABS.map(({ key, labelKey, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active === key}
            onClick={() => setActive(key)}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
              active === key
                ? "bg-brand-blue text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Icon className="h-4 w-4" />
            {t(labelKey)}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6">
        {active === "personal" && <PersonalTab emp={data} />}
        {active === "identity" && <IdentityTab emp={data} />}
        {active === "contact" && <ContactTab emp={data} />}
        {active === "family" && <FamilyTab emp={data} />}
        {active === "banking" && <BankingTab emp={data} />}
        {active === "documents" && (
          <EmployeeDocumentsTab employeeId={data.id} />
        )}
        {active === "compliance" && <ComplianceTab emp={data} />}
        {active === "history" && <HistoryTab emp={data} />}
      </div>
    </div>
  );
}
